<script setup>
/**
 * "Buy credits": pick a pack, pick KNET or a bank card, and leave for the payment page.
 * The money side is the Rukn payments service; this page only asks the BFF for a checkout
 * and follows its `url`. The payer comes back to `CreditsReturnPage`.
 *
 * When paying online is not open (no keys on this environment, or one of our own
 * workspaces) the page says so and offers the old "Request top-up" instead.
 */
import { onBeforeUnmount, onMounted, ref } from 'vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import WasloPageLayout from '../components/WasloPageLayout.vue';
import TopUpRequestButton from '../components/TopUpRequestButton.vue';
import { useWasloI18n } from '../i18n.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { wasloApi } from '../api/client.js';
import { leaveTo } from '../helpers/claude.js';
import { formatCount } from '../helpers/plural.js';

const { t, locale } = useWasloI18n();
const accountId = useWasloAccountId();
const purchase = ref(null);
const loading = ref(true);
const failure = ref('');
// `${packId}:${method}` while that checkout is being opened, so only its button spins.
const busy = ref('');
const leaving = ref(false);

const load = async () => {
  loading.value = true;
  failure.value = '';
  try {
    if (accountId.value === null) return;
    purchase.value = await wasloApi.getCreditPurchase(accountId.value);
  } catch {
    failure.value = t('WASLO.BUY_CREDITS.LOAD_FAILED');
  } finally {
    loading.value = false;
  }
};
// Back from the payment page can restore this page from the back/forward cache with
// `leaving` and `busy` still set, which would leave both pay buttons disabled until a reload.
const onPageShow = event => {
  if (!event.persisted) return;
  leaving.value = false;
  busy.value = '';
};
onMounted(() => {
  window.addEventListener('pageshow', onPageShow);
  load();
});
onBeforeUnmount(() => window.removeEventListener('pageshow', onPageShow));

const creditsOf = pack =>
  t('WASLO.AI_CONFIRM.CREDITS', { count: pack.credits });
const priceOf = pack =>
  t('WASLO.BUY_CREDITS.PRICE', {
    amount: formatCount(pack.priceFils / 1000, locale.value, {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }),
  });

const buy = async (pack, method) => {
  if (accountId.value === null || busy.value || leaving.value) return;
  busy.value = `${pack.id}:${method}`;
  failure.value = '';
  try {
    const checkout = await wasloApi.startCreditCheckout(
      accountId.value,
      pack.id,
      method
    );
    leaving.value = true;
    leaveTo(checkout.url);
  } catch {
    failure.value = t('WASLO.BUY_CREDITS.START_FAILED');
  } finally {
    busy.value = '';
  }
};
</script>

<template>
  <WasloPageLayout
    :header-title="t('WASLO.BUY_CREDITS.TITLE')"
    :is-fetching="loading"
  >
    <template #body>
      <div
        class="flex flex-col gap-6 pb-10 max-w-[62ch]"
        data-waslo-credits-buy
      >
        <Banner
          v-if="purchase && purchase.testMode"
          color="amber"
          data-waslo-credits-test-mode
        >
          {{ t('WASLO.BUY_CREDITS.TEST_MODE') }}
        </Banner>
        <template v-if="purchase && purchase.available">
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('WASLO.BUY_CREDITS.INTRO') }}
          </p>
          <ul class="flex flex-col gap-3 mb-0 list-none ps-0">
            <li
              v-for="pack in purchase.packs"
              :key="pack.id"
              :data-waslo-credit-pack="pack.id"
            >
              <CardLayout>
                <div class="flex flex-wrap items-center justify-between gap-3">
                  <div class="flex flex-col">
                    <span class="text-base font-medium text-n-slate-12">
                      {{ creditsOf(pack) }}
                    </span>
                    <span class="text-sm text-n-slate-11">
                      {{ priceOf(pack) }}
                    </span>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      :label="t('WASLO.BUY_CREDITS.PAY_KNET')"
                      :is-loading="busy === `${pack.id}:knet`"
                      :disabled="Boolean(busy) || leaving"
                      data-waslo-pay-knet
                      @click="buy(pack, 'knet')"
                    />
                    <Button
                      size="sm"
                      variant="faded"
                      color="slate"
                      :label="t('WASLO.BUY_CREDITS.PAY_CARD')"
                      :is-loading="busy === `${pack.id}:card`"
                      :disabled="Boolean(busy) || leaving"
                      data-waslo-pay-card
                      @click="buy(pack, 'card')"
                    />
                  </div>
                </div>
              </CardLayout>
            </li>
          </ul>
        </template>
        <template v-else-if="purchase">
          <p
            class="mb-0 text-sm text-n-slate-11"
            data-waslo-credits-unavailable
          >
            {{ t('WASLO.BUY_CREDITS.UNAVAILABLE') }}
          </p>
          <TopUpRequestButton :account-id="accountId" />
        </template>
        <p
          v-if="failure"
          class="mb-0 text-sm text-n-ruby-11"
          data-waslo-credits-failure
        >
          {{ failure }}
        </p>
      </div>
    </template>
  </WasloPageLayout>
</template>
